import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import MobileLayout from "@/components/MobileLayout";
import api, { SESSION_ID, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, FileText, Trash2, X, Save, Clock, Lock, Unlock, AlertTriangle,
  CheckCircle2, Archive, MessageCircle, Send as SendIcon,
} from "lucide-react";
import CopiesInput from "@/components/CopiesInput";

const statusColors = {
  "Recibido": "bg-blue-100 text-blue-800 border-blue-200",
  "Pendiente de revisar": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Falta información": "bg-orange-100 text-orange-800 border-orange-200",
  "Listo para imprimir": "bg-purple-100 text-purple-800 border-purple-200",
  "En preparación": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Terminado": "bg-green-100 text-green-800 border-green-200",
  "Entregado": "bg-slate-200 text-slate-700 border-slate-300",
  "Cancelado": "bg-red-100 text-red-800 border-red-200",
  "Incidencia": "bg-rose-100 text-rose-800 border-rose-200",
};

const CLIENT_EDITABLE = new Set(["Recibido", "Falta información"]);
const SIZE_OPTS = ["A4", "A3", "Otro"];
const PAPER_OPTS = [
  { value: "normal", label: "Normal" },
  { value: "cartulina", label: "Cartulina" },
  { value: "foto", label: "Fotográfico" },
  { value: "pegatina", label: "Pegatina" },
  { value: "otro", label: "Otro" },
];

// Helper para los RadioGroups coherentes con Send.jsx
function OptionRow({ label, name, opts, value, onChange }) {
  return (
    <div>
      <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">{label}</Label>
      <RadioGroup
        value={value}
        onValueChange={onChange}
        className="mt-2 grid grid-cols-2 gap-2"
      >
        {opts.map((o) => (
          <label
            key={o.value}
            data-testid={`edit-${name}-${o.value}`}
            className={`flex cursor-pointer items-center gap-2 rounded-xl border-2 p-3 text-base font-bold ${
              value === o.value
                ? "border-[#4d7c0f] bg-[#4d7c0f]/5 text-[#4d7c0f]"
                : "border-slate-200 text-slate-700"
            }`}
          >
            <RadioGroupItem value={o.value} className="sr-only" />
            {o.label}
          </label>
        ))}
      </RadioGroup>
    </div>
  );
}

export default function OrderDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const paymentReturn = new URLSearchParams(location.search).get("payment");
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [optsDraft, setOptsDraft] = useState({});
  const [sizeOther, setSizeOther] = useState("");
  const [paperOther, setPaperOther] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // filename pendiente

  const editable = useMemo(
    () => order && CLIENT_EDITABLE.has(order.status) && !order.viewed_at,
    [order]
  );
  const isOpen = useMemo(() => {
    if (!order?.is_open) return false;
    if (!order?.expires_at) return false;
    return new Date(order.expires_at) > new Date();
  }, [order]);

  // Cuando opts.size === "Otro: ...", desglosamos para que el RadioGroup
  // muestre "Otro" seleccionado y el Input adicional muestre el texto.
  const parsedSize = useMemo(() => {
    const s = optsDraft.size || "A4";
    if (typeof s === "string" && s.startsWith("Otro:")) {
      return { base: "Otro", extra: s.slice(5).trim() };
    }
    return { base: SIZE_OPTS.includes(s) ? s : "Otro", extra: SIZE_OPTS.includes(s) ? "" : s };
  }, [optsDraft.size]);

  const parsedPaper = useMemo(() => {
    const p = optsDraft.paper || "normal";
    if (typeof p === "string" && p.startsWith("otro:")) {
      return { base: "otro", extra: p.slice(5).trim() };
    }
    const valid = PAPER_OPTS.map((o) => o.value);
    return {
      base: valid.includes(p) ? p : "otro",
      extra: valid.includes(p) ? "" : p,
    };
  }, [optsDraft.paper]);

  const reload = async () => {
    setLoading(true);
    setErr("");
    try {
      const url = user
        ? "/orders/mine"
        : `/orders/guest?sid=${encodeURIComponent(SESSION_ID)}`;
      const { data } = await api.get(url);
      const found = data.find((o) => o.code === code);
      if (!found) {
        setErr("Pedido no encontrado o sin acceso.");
        setOrder(null);
      } else {
        setOrder(found);
        setNoteDraft(found.note || "");
        setOptsDraft(found.options || {});
        if (found.options?.size && String(found.options.size).startsWith("Otro:")) {
          setSizeOther(String(found.options.size).slice(5).trim());
        }
        if (found.options?.paper && String(found.options.paper).startsWith("otro:")) {
          setPaperOther(String(found.options.paper).slice(5).trim());
        }
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error de red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, location.search]);

  useEffect(() => {
    if (paymentReturn === "success") {
      const t1 = setTimeout(() => reload(), 1500);
      const t2 = setTimeout(() => reload(), 4000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentReturn]);

  const saveNote = async () => {
    if (!order || !editable) return;
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.put(`/orders/${order.id}`, { note: noteDraft });
      setOrder(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const saveOpts = async () => {
    if (!order || !editable || order.mode !== "configured") return;
    setSaving(true);
    setErr("");
    try {
      const body = {
        copies: optsDraft.copies,
        color: optsDraft.color,
        sides: optsDraft.sides,
        size: parsedSize.base,
        paper: parsedPaper.base,
      };
      if (parsedSize.base === "Otro" && sizeOther.trim()) body.size_other = sizeOther.trim();
      if (parsedPaper.base === "otro" && paperOther.trim()) body.paper_other = paperOther.trim();
      const { data } = await api.put(`/orders/${order.id}`, body);
      setOrder(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const askRemoveFile = (file) => setConfirmRemove(file);

  const doRemoveFile = async () => {
    if (!order || !editable || !confirmRemove) return;
    const target = confirmRemove;
    setConfirmRemove(null);
    setSaving(true);
    setErr("");
    try {
      // Usamos file_id (estable) en lugar del filename para evitar errores
      // con nombres con espacios, paréntesis, acentos o caracteres especiales.
      // Si por compatibilidad un fichero antiguo no tiene file_id, caemos al
      // filename.
      const body = target.file_id
        ? { file_id: target.file_id }
        : { filename: target.filename };
      const { data } = await api.post(`/orders/${order.id}/remove-file`, body);
      setOrder(data);
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail);
      setErr(msg || "No se pudo eliminar. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const cancelOrder = async () => {
    if (!order || !editable) return;
    setSaving(true);
    setConfirmCancel(false);
    try {
      const { data } = await api.post(`/orders/${order.id}/cancel`);
      setOrder(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "No se pudo cancelar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout>
        <p className="text-slate-500">Cargando…</p>
      </MobileLayout>
    );
  }

  if (err && !order) {
    return (
      <MobileLayout>
        <button
          onClick={() => navigate("/codes")}
          className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-slate-600"
          data-testid="detail-back"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
        <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-4 text-red-800">
          {err}
        </div>
      </MobileLayout>
    );
  }

  if (!order) return null;

  return (
    <MobileLayout>
      <button
        onClick={() => navigate("/codes")}
        className="mb-3 inline-flex items-center gap-1 text-sm font-bold text-slate-600"
        data-testid="detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> Mis códigos
      </button>

      <div className="mb-4">
        <div
          data-testid="detail-code"
          className="inline-block rounded-2xl border-4 border-[#4d7c0f] bg-[#4d7c0f]/5 px-6 py-4 text-4xl font-black tracking-wider text-[#4d7c0f]"
        >
          {order.code}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge
          data-testid="detail-status"
          variant="outline"
          className={`border ${statusColors[order.status] || "bg-slate-100 text-slate-700"}`}
        >
          {order.status}
        </Badge>
        {editable ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#4d7c0f]">
            <Unlock className="h-3 w-3" /> Editable
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500">
            <Lock className="h-3 w-3" /> Bloqueado
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
          <Clock className="h-3 w-3" />
          {new Date(order.created_at).toLocaleString("es-ES")}
        </span>
      </div>

      {paymentReturn === "success" && order.payment_status !== "paid" && (
        <section className="mb-4 rounded-2xl border-2 border-blue-300 bg-blue-50 p-4 text-blue-950">
          <div className="text-lg font-black">⏳ Confirmando pago</div>
          <p className="mt-1 text-sm font-semibold">
            Hemos recibido la vuelta de la pasarela de pago. Estamos confirmando el pago de forma segura.
          </p>
          <p className="mt-2 text-xs font-bold text-blue-800">
            Si ya has pagado, no vuelvas a pagar todavía. Espera unos segundos o actualiza el pedido.
          </p>
          <button
            type="button"
            onClick={reload}
            className="mt-3 rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white"
          >
            Actualizar estado
          </button>
        </section>
      )}

      {paymentReturn === "cancelled" && order.payment_status !== "paid" && (
        <section className="mb-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 text-amber-950">
          <div className="text-lg font-black">⚠️ Pago no completado</div>
          <p className="mt-1 text-sm font-semibold">
            El pago no se ha realizado o se ha cancelado antes de finalizar.
          </p>
          <p className="mt-2 text-xs font-bold text-amber-800">
            No debería haberse realizado ningún cargo. Puedes volver a intentarlo desde el botón de pago.
          </p>
        </section>
      )}

      {order.payment_status && order.payment_status !== "none" && (
        <section
          data-testid="payment-box"
          className={`mb-4 rounded-2xl border-2 p-4 shadow-sm ${
            order.payment_status === "paid"
              ? "border-emerald-300 bg-emerald-50"
              : "border-emerald-300 bg-emerald-50"
          }`}
        >
          {order.payment_status === "paid" ? (
            <>
              <div className="mb-2 flex items-center gap-2 text-lg font-black text-emerald-900">
                <CheckCircle2 className="h-6 w-6" />
                Pedido pagado
              </div>
              <p className="text-sm font-semibold text-emerald-900">
                Hemos recibido correctamente el pago de este pedido.
              </p>
            </>
          ) : (
            <>
              <div className="mb-2 flex items-center gap-2 text-lg font-black text-emerald-950">
                <Lock className="h-6 w-6" />
                Pago seguro online
              </div>

              <p className="text-sm font-bold text-emerald-950">
                Puede pagar este pedido de forma segura con tarjeta.
              </p>

              <ul className="mt-3 space-y-1 text-sm font-semibold text-emerald-900">
                <li>✔ Conexión cifrada y protegida</li>
                <li>✔ KAPEI no almacena los datos de su tarjeta</li>
                <li>✔ Compatible con Visa y Mastercard</li>
                <li>✔ Confirmación inmediata del pago</li>
              </ul>

              {order.payment_note && (
                <div className="mt-3 rounded-xl bg-white/80 p-3 text-sm font-bold text-emerald-950">
                  {order.payment_note}
                </div>
              )}

              <div className="mt-4 rounded-xl bg-white/80 p-3">
                <div className="text-xs font-black uppercase tracking-widest text-emerald-700">
                  Importe pendiente
                </div>
                <div className="text-3xl font-black text-emerald-950">
                  {Number(order.payment_amount_eur || 0).toFixed(2)} €
                </div>
              </div>

              {order.payment_checkout_url ? (
                <a
                  href={order.payment_checkout_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="payment-pay-now"
                  className="mt-4 block rounded-2xl bg-[#4d7c0f] px-5 py-4 text-center text-lg font-black text-white shadow-lg hover:bg-[#3f6212]"
                >
                  PAGAR AHORA
                </a>
              ) : (
                <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm font-bold text-amber-900">
                  El enlace de pago se está preparando. Vuelve a abrir el pedido en unos segundos.
                </div>
              )}

              <p className="mt-3 text-xs font-semibold text-emerald-800">
                Pago procesado mediante una plataforma de pago segura.
              </p>
            </>
          )}
        </section>
      )}

      {/* Hilo de mensajes ligado al pedido */}
      <Thread order={order} editable={editable} onUpdate={setOrder} setErr={setErr} />

      {!editable && (
        <div
          data-testid="not-editable-banner"
          className="mb-4 flex items-start gap-2 rounded-2xl border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            KAPEI ya está revisando este pedido. No puedes cambiar archivos ni opciones,
            pero sí puedes responder aquí si KAPEI te ha escrito.
          </div>
        </div>
      )}

      {err && (
        <div className="mb-4 rounded-2xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {err}
        </div>
      )}

      {/* Cliente */}
      <section className="mb-4 rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-slate-500">
          A nombre de
        </h2>
        <p className="text-base text-slate-800">
          {order.client_name && order.client_name !== order.code
            ? order.client_name
            : "Invitado"}
        </p>
      </section>

      {/* Archivos activos */}
      <section className="mb-4 rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-500">
          Archivos del pedido ({order.files?.length || 0})
        </h2>
        <ul className="space-y-2" data-testid="detail-files">
          {(order.files || []).map((f, i) => (
            <li
              key={`${f.filename}-${i}`}
              data-testid={`detail-file-${i}`}
              className="flex items-center justify-between gap-2 rounded-xl border-2 border-[#4d7c0f]/30 bg-[#4d7c0f]/5 p-3"
            >
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-5 w-5 shrink-0 text-[#4d7c0f]" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-slate-800">
                    {f.filename}
                  </div>
                  <div className="text-xs text-slate-500">
                    {Math.round((f.size || 0) / 1024)} KB
                    {typeof f.pages === "number" && ` · ${f.pages} pág.`}
                  </div>
                </div>
              </div>
              {editable && (
                <button
                  type="button"
                  onClick={() => askRemoveFile(f)}
                  data-testid={`remove-file-${i}`}
                  className="rounded-full p-2 text-red-500 hover:bg-red-50"
                  aria-label={`Quitar ${f.filename}`}
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </li>
          ))}
          {(order.files || []).length === 0 && (
            <li className="text-sm italic text-slate-500">Sin archivos.</li>
          )}
        </ul>
        {order.page_count != null && (
          <p className="mt-2 text-xs text-slate-500">
            Total páginas: <strong>{order.page_count}</strong>
          </p>
        )}
        {editable && isOpen && (
          <Link
            to={`/send?mode=${order.mode || "quick"}&append=${order.id}`}
            data-testid="detail-add-files"
            className="mt-3 inline-block rounded-full border-2 border-[#4d7c0f] px-3 py-1 text-xs font-bold text-[#4d7c0f]"
          >
            Añadir más archivos
          </Link>
        )}
      </section>

      {/* Auditoría: archivos eliminados (solo si los hay) */}
      {Array.isArray(order.removed_files) && order.removed_files.length > 0 && (
        <section
          data-testid="removed-files-section"
          className="mb-4 rounded-2xl border-2 border-slate-200 bg-slate-50 p-4"
        >
          <h2 className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
            <Archive className="h-4 w-4" /> Archivos eliminados — NO IMPRIMIR
          </h2>
          <ul className="space-y-1 text-xs text-slate-500">
            {order.removed_files.map((f, i) => (
              <li key={i} className="flex items-center gap-2 line-through">
                <FileText className="h-3 w-3" />
                {f.filename}
                {f.removed_at && (
                  <span className="text-[10px] text-slate-400">
                    ({new Date(f.removed_at).toLocaleString("es-ES")})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Opciones de impresión */}
      {order.mode === "configured" && (
        <section className="mb-4 rounded-2xl border-2 border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-500">
            Preferencias de impresión
          </h2>
          {editable ? (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">
                  Copias
                </Label>
                <div className="mt-2">
                  <CopiesInput
                    value={optsDraft.copies ?? 1}
                    onChange={(n) => setOptsDraft({ ...optsDraft, copies: n })}
                    testid="edit-copies"
                  />
                </div>
              </div>

              <OptionRow
                label="Tinta"
                name="color"
                opts={[
                  { value: "bw", label: "Blanco y negro" },
                  { value: "color", label: "Color" },
                ]}
                value={optsDraft.color || "bw"}
                onChange={(v) => setOptsDraft({ ...optsDraft, color: v })}
              />

              <OptionRow
                label="Caras"
                name="sides"
                opts={[
                  { value: "single", label: "Una cara" },
                  { value: "double", label: "Dos caras" },
                ]}
                value={optsDraft.sides || "double"}
                onChange={(v) => setOptsDraft({ ...optsDraft, sides: v })}
              />

              <OptionRow
                label="Tamaño"
                name="size"
                opts={SIZE_OPTS.map((s) => ({ value: s, label: s }))}
                value={parsedSize.base}
                onChange={(v) => setOptsDraft({ ...optsDraft, size: v })}
              />
              {parsedSize.base === "Otro" && (
                <Input
                  value={sizeOther || parsedSize.extra}
                  onChange={(e) => setSizeOther(e.target.value)}
                  placeholder="Indica el tamaño exacto"
                  data-testid="edit-size-other"
                />
              )}

              <OptionRow
                label="Papel"
                name="paper"
                opts={PAPER_OPTS}
                value={parsedPaper.base}
                onChange={(v) => setOptsDraft({ ...optsDraft, paper: v })}
              />
              {parsedPaper.base === "otro" && (
                <Input
                  value={paperOther || parsedPaper.extra}
                  onChange={(e) => setPaperOther(e.target.value)}
                  placeholder="Indica el tipo de papel"
                  data-testid="edit-paper-other"
                />
              )}

              <Button
                onClick={saveOpts}
                disabled={saving}
                data-testid="save-opts"
                className="w-full bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
              >
                <Save className="mr-2 h-4 w-4" /> Guardar preferencias
              </Button>
            </div>
          ) : (
            <ul className="space-y-1 text-sm text-slate-700">
              <li>Copias: {order.options?.copies}</li>
              <li>Tinta: {order.options?.color === "bw" ? "Blanco y negro" : "Color"}</li>
              <li>Caras: {order.options?.sides === "single" ? "Una cara" : "Dos caras"}</li>
              <li>Tamaño: {order.options?.size}</li>
              <li>Papel: {order.options?.paper}</li>
            </ul>
          )}
        </section>
      )}

      {/* Nota */}
      <section className="mb-4 rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-slate-500">
          Nota
        </h2>
        {editable ? (
          <>
            <Textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Ej: Imprimir solo páginas 1-5, grapado…"
              data-testid="edit-note"
              className="min-h-[80px]"
            />
            <Button
              onClick={saveNote}
              disabled={saving || noteDraft === (order.note || "")}
              data-testid="save-note"
              className="mt-2 w-full bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
            >
              <Save className="mr-2 h-4 w-4" /> Guardar nota
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-700">
            {order.note ? order.note : <span className="italic text-slate-400">Sin nota.</span>}
          </p>
        )}
      </section>

      {/* Mensaje del staff (legacy: campo único). Si hay thread, se ignora. */}
      {!order.thread?.length && (order.incident || order.last_admin_note) && (
        <section
          data-testid="staff-message"
          className="mb-4 rounded-2xl border-2 border-rose-200 bg-rose-50 p-4"
        >
          <h2 className="mb-1 text-sm font-bold uppercase tracking-widest text-rose-700">
            Mensaje de KAPEI
          </h2>
          <p className="text-sm text-rose-900">
            {order.incident || order.last_admin_note}
          </p>
        </section>
      )}

      {/* Acciones finales */}
      <section className="mb-8 flex flex-col gap-3">
        <Button
          onClick={() => navigate("/codes")}
          data-testid="confirm-ok"
          className="h-14 w-full bg-[#4d7c0f] text-base font-bold text-white hover:bg-[#3f6212]"
        >
          <CheckCircle2 className="mr-2 h-5 w-5" /> Es correcto, continuar
        </Button>
        {editable && (
          <Button
            variant="outline"
            onClick={() => setConfirmCancel(true)}
            disabled={saving}
            data-testid="cancel-order"
            className="w-full border-2 border-red-300 text-red-600 hover:bg-red-50"
          >
            <X className="mr-2 h-4 w-4" /> Cancelar pedido
          </Button>
        )}
      </section>

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Cancelar el pedido {order.code}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Esta acción no se puede deshacer. KAPEI no procesará este pedido.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(false)}>
              No
            </Button>
            <Button
              onClick={cancelOrder}
              data-testid="confirm-cancel"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Sí, cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Quitar archivo del pedido?</DialogTitle>
          </DialogHeader>
          <p className="break-all text-sm text-slate-600">
            <code className="font-mono">{confirmRemove?.filename}</code>
          </p>
          <p className="text-xs text-slate-500">
            KAPEI no imprimirá este archivo. Quedará registrado en el historial
            del pedido como eliminado.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)}>
              No
            </Button>
            <Button
              onClick={doRemoveFile}
              data-testid="confirm-remove-file"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Sí, quitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}

function Thread({ order, editable, onUpdate, setErr }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const thread = order.thread || [];

  // Marca mensajes del staff como leídos al ver el hilo
  useEffect(() => {
    if (!order.has_unread_for_client) return;
    api.post(`/orders/${order.id}/messages/mark-read`).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const send = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/messages`, { body });
      onUpdate(data);
      setReply("");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "No se pudo enviar la respuesta");
    } finally {
      setSending(false);
    }
  };

  const canReply = true;

  if (thread.length === 0 && !canReply) return null;

  return (
    <section
      data-testid="order-thread"
      className="mb-4 rounded-2xl border-2 border-slate-200 bg-white p-4"
    >
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-600">
        <MessageCircle className="h-4 w-4" /> Mensajes
      </h2>

      {thread.length === 0 ? (
        <p className="mb-3 text-sm italic text-slate-500">
          Aún no hay mensajes en este pedido.
        </p>
      ) : (
        <ul className="mb-3 space-y-2" data-testid="thread-messages">
          {thread.map((m) => {
            const isStaff = m.author_role === "staff";
            const isLong = (m.body || "").length > 220;
            const expanded = expandedId === m.id;
            const display = expanded || !isLong ? m.body : `${m.body.slice(0, 220)}…`;
            return (
              <li
                key={m.id}
                data-testid={`thread-msg-${m.author_role}`}
                className={`rounded-2xl p-3 text-sm ${
                  isStaff
                    ? "border-2 border-rose-200 bg-rose-50 text-rose-900"
                    : "border-2 border-[#4d7c0f]/30 bg-[#4d7c0f]/5 text-slate-800"
                }`}
              >
                <div className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest">
                  <span>{isStaff ? "KAPEI" : m.author_name || "Tú"}</span>
                  <span className="text-slate-400">
                    {new Date(m.created_at).toLocaleString("es-ES")}
                  </span>
                </div>
                <p className="whitespace-pre-wrap break-words">{display}</p>
                {isLong && (
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : m.id)}
                    className="mt-1 text-xs font-bold underline"
                  >
                    {expanded ? "Ver menos" : "Ver mensaje completo"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canReply && (
        <div className="space-y-2">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Responder a KAPEI…"
            data-testid="thread-reply"
            className="min-h-[80px]"
            maxLength={2000}
          />
          <Button
            onClick={send}
            disabled={sending || !reply.trim()}
            data-testid="thread-send"
            className="w-full bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
          >
            <SendIcon className="mr-2 h-4 w-4" /> Enviar respuesta
          </Button>
        </div>
      )}
    </section>
  );
}

